import { lazy } from 'react';

import { IconProvider } from '@actualtysoft/ui-kit';
import {
  faCalendarDays,
  faCogs,
  faHome,
} from '@fortawesome/pro-solid-svg-icons';
import { NavRoute } from 'data/models/navigation.interface';
import { User } from 'data/models/user.interface';
import compact from 'lodash/compact';
import flatMap from 'lodash/flatMap';
import isEmpty from 'lodash/isEmpty';
import ActivitySkeleton from 'pages/Activity/ListSkeleton';
import ListSkeletonReferential from 'pages/Referential/ListSkeleton';
import ListSkeletonValues from 'pages/Values/ListSkeleton';

const ActivityList = lazy(() => import('pages/Activity/List'));
const ReferentialList = lazy(() => import('pages/Referential/List'));
const ValueList = lazy(() => import('pages/Values/List'));
const Dashboard = lazy(() => import('pages/Dashboard/Home'));

export const routes: NavRoute[] = [
  {
    name: 'Mon dashboard',
    path: '/dashboard',
    component: <Dashboard />,
    icon: <IconProvider icon={faHome} size="md" />,
  },
  {
    name: 'Liste des référentiels',
    path: '/referentials',
    permissions: ['atlas.lecture'],
    component: <ReferentialList />,
    skeleton: <ListSkeletonReferential />,
    icon: <IconProvider icon={faCogs} size="md" />,
  },
  {
    name: 'Valeurs de referentiel',
    path: '/referentials/:id',
    hidden: true,
    permissions: ['atlas.lecture'],
    component: <ValueList />,
    skeleton: <ListSkeletonValues />,
    icon: <IconProvider icon={faCogs} size="md" />,
  },
  {
    name: 'Activités',
    path: '/activity',
    permissions: ['atlas.lecture'],
    component: <ActivityList />,
    skeleton: <ActivitySkeleton />,
    icon: <IconProvider icon={faCalendarDays} size="sm" />,
  },
];

const flattenRoutes = (
  routes: Array<NavRoute & { ancestor?: NavRoute[] }>,
  path = '',
  ancestor?: NavRoute[],
): Array<NavRoute & { ancestors: NavRoute[] }> => {
  return flatMap(
    routes.map((route) => {
      const fullPath = (path && `${path}${route.path}`) || route.path;
      const ancestors = compact([
        ...(ancestor || []),
        {
          ...route,
          path: fullPath,
        },
      ]);
      const fullRoute = {
        ...route,
        path: fullPath,
        ancestors,
      };
      if (isEmpty(route?.children)) {
        return fullRoute;
      }
      return compact([
        ...flattenRoutes(route.children || [], fullPath, ancestors),
        (route.component && fullRoute) || null,
      ]);
    }),
  );
};

export const flatRoutes = flattenRoutes(routes);

export const getUserRoutes = (
  user: User,
): Array<NavRoute & { ancestors: NavRoute[] }> =>
  flatRoutes.filter((route) =>
    (route.permissions || ['*']).some(
      (permission) =>
        permission === '*' || user.permissions.includes(permission),
    ),
  );

export const getRouteByPathname = (
  pathname: string,
): (NavRoute & { ancestors: NavRoute[] }) | undefined => {
  return flatRoutes.find(({ path }) => {
    const pattern = '^' + path.replace(/:[^/]+/g, '[^/]+') + '$';
    const regex = new RegExp(pattern);
    return regex.test(pathname);
  });
};
